import React, { useEffect, useMemo, useState } from "react";

import { BookOpen, ChevronUp } from "lucide-react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import RadioButton from "../../Components/RadioButton";
import TextArea from "../../Components/TextArea";
import XFactorLevelGuide from "../../Container/TemplateContainer/components/XFactorLevelGuide";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  DisconnectResolution,
  recordDisconnectResolution,
} from "../../Store/Reducers/AssessmentSlice";
import { getLevelCompactName } from "../../Utils/Helper/levelUtils";
import { toast } from "../../Utils/Helper/toast";

const FALLBACK_GUIDANCE =
  "Provide your rationale and key evidence that supports this level rating.";

export interface RecordResolutionModalProps {
  isOpen: boolean;
  resolution: DisconnectResolution | null;
  onClose: () => void;
  onSuccess: (resolution: DisconnectResolution) => void;
}

const RecordResolutionModal: React.FC<RecordResolutionModalProps> = ({
  isOpen,
  resolution,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const organizationLevels = useAppSelector(
    state => state.admin.organizationLevels
  );
  const isRecordingResolution = useAppSelector(
    state => state.assessment.isRecordingResolution
  );

  const [selectedLevelCode, setSelectedLevelCode] = useState<string>("");
  const [justification, setJustification] = useState("");
  const [showGuidanceDrawer, setShowGuidanceDrawer] = useState(false);
  const [rationaleGuidance, setRationaleGuidance] = useState(FALLBACK_GUIDANCE);

  useEffect(() => {
    if (isOpen && organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, isOpen, organizationLevels.length]);

  const isEditMode = resolution?.status === "resolved";

  useEffect(() => {
    if (!isOpen || !resolution) {
      setSelectedLevelCode("");
      setJustification("");
      setShowGuidanceDrawer(false);
      setRationaleGuidance(FALLBACK_GUIDANCE);
      return;
    }
    if (
      isEditMode &&
      typeof resolution.agreed_level === "number" &&
      Number.isFinite(resolution.agreed_level)
    ) {
      setSelectedLevelCode(`L${resolution.agreed_level}`);
      setJustification(resolution.resolution_justification?.trim() || "");
    } else {
      setSelectedLevelCode("");
      setJustification("");
    }
    setShowGuidanceDrawer(false);
    setRationaleGuidance(FALLBACK_GUIDANCE);
  }, [isOpen, resolution, isEditMode]);

  const agreedLevelNumber = useMemo(() => {
    if (!selectedLevelCode) return null;
    const num = Number(String(selectedLevelCode).replace(/^L/i, ""));
    return Number.isFinite(num) ? num : null;
  }, [selectedLevelCode]);

  const canSubmit =
    !!resolution &&
    agreedLevelNumber !== null &&
    justification.trim().length > 0 &&
    !isRecordingResolution;

  const handleSubmit = async () => {
    if (!resolution || !canSubmit || agreedLevelNumber === null) return;

    try {
      await dispatch(
        recordDisconnectResolution({
          resolutionId: resolution.id,
          responseType: "override_assessment",
          agreedLevel: agreedLevelNumber,
          justification: justification.trim(),
        })
      ).unwrap();

      onSuccess(resolution);
      onClose();
    } catch (error: any) {
      toast.error(
        typeof error === "string" ? error : "Failed to record resolution"
      );
    }
  };

  if (!resolution) return null;

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      size="6xl"
      title={`${isEditMode ? "Edit" : "Record"} Resolution: ${resolution.accountName || "Account"}`}
      contentClassName="overflow-y-auto max-h-[70vh]"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="outline-primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isRecordingResolution
              ? "Submitting…"
              : isEditMode
                ? "Update Resolution"
                : "Submit Resolution"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <p className="mb-0 -mt-1 text-sm leading-relaxed text-gray-500">
          {isEditMode
            ? "Update your agreed level or justification until the disconnect due date."
            : "Review sponsor assessments and submit your level against the published level."}
        </p>
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900">
            Agreed Level <span className="text-red-500">*</span>
          </h4>
          <XFactorLevelGuide
            showHeader
            compact
            organizationLevels={organizationLevels}
            selectedLevel={selectedLevelCode || null}
            className="mb-4"
          />
          <p className="mb-4 text-sm text-gray-600">
            Use this XFACTOR framework to select the current level of alignment
            maturity for each account included in this account-level assessment.
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {organizationLevels.map(level => {
              const levelCode =
                level.level_code || (level as { levels?: string }).levels || "";
              const isSelected = selectedLevelCode === levelCode;
              return (
                <div
                  key={levelCode}
                  className={`min-w-0 rounded-lg border p-3 transition-all ${
                    isSelected
                      ? "border-[#974ae4] bg-[#974ae4]/5 shadow-sm"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <RadioButton
                    name="agreed-level"
                    value={levelCode}
                    label={
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium text-gray-900">
                          {getLevelCompactName(levelCode)}
                        </span>
                        <span className="truncate text-sm text-gray-600">
                          {level.name}
                        </span>
                      </span>
                    }
                    checked={isSelected}
                    onChange={e => {
                      const selected = e.target.value;
                      setSelectedLevelCode(selected);
                      const guidance =
                        (level as { guidance?: string }).guidance ||
                        FALLBACK_GUIDANCE;
                      setRationaleGuidance(guidance);
                      setShowGuidanceDrawer(true);
                    }}
                  />
                </div>
              );
            })}
          </div>
          {selectedLevelCode && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowGuidanceDrawer(v => !v)}
                className="mb-2 flex items-center gap-1.5 text-sm font-medium text-[#4f46e5] transition-opacity hover:opacity-80"
              >
                <BookOpen size={14} />
                Rationale guidance
                <ChevronUp
                  size={14}
                  className={`transition-transform ${showGuidanceDrawer ? "" : "rotate-180"}`}
                />
              </button>
              {showGuidanceDrawer && (
                <div className="rounded-xl border border-[#e0e0f0] bg-[#eef0fb] px-4 py-3 text-sm text-[#3730a3]">
                  {rationaleGuidance}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-semibold text-gray-900">
            Justification <span className="text-red-500">*</span>
          </label>
          <TextArea
            name="justification"
            value={justification}
            onChange={e => setJustification(e.target.value)}
            placeholder="Provide your rationale and key evidence that supports this level rating."
            rows={5}
            className="w-full"
          />
        </div>
      </div>
    </CommonModal>
  );
};

export default RecordResolutionModal;
